import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { getUsersName } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, Select, TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BORDER = 'var(--vo-steel)';
const TEXT = 'var(--vo-ink-2)';
const DIVIDER = 'var(--vo-cloud)';

const Card = styled.div`
  max-width: 800px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const IconTile = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-success), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;
const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
`;
const Label = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;
const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin: 0;
`;
const ChipInputWrap = styled.div`
  min-height: 40px;
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  padding: 8px 12px;
  background: white;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
  cursor: text;
  &:focus-within { border-color: ${BLUE}; }
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--vo-capri-50);
  color: var(--vo-celeste);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
`;
const ChipXBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: var(--vo-celeste);
`;
const ChipInner = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  flex: 1;
  min-width: 80px;
  &::placeholder { color: ${MUTED}; }
`;
const PiiCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  background: var(--vo-danger-10);
  border-radius: 10px;
  border: 1px solid var(--vo-danger-15);
`;

const BoxSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
  </svg>
);
const XSvg = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TagInput = ({ value, onChange, placeholder }) => {
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);
  const tags = Array.isArray(value) ? value : [];

  const addTag = () => {
    const trimmed = inputVal.trim().replace(/,$/, '');
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
    if (e.key === 'Backspace' && inputVal === '' && tags.length > 0) onChange(tags.slice(0, -1));
  };

  return (
    <ChipInputWrap onClick={() => inputRef.current?.focus()}>
      {tags.map(tag => (
        <Chip key={tag}>
          {tag}
          <ChipXBtn type="button" onClick={(e) => { e.preventDefault(); onChange(tags.filter(t => t !== tag)); }}>
            <XSvg />
          </ChipXBtn>
        </Chip>
      ))}
      <ChipInner
        ref={inputRef}
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </ChipInputWrap>
  );
};

export const Detalhes = ({ register, control, errors, getValues, id }) => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);

  const { data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data?.length > 0 ? result.data : [];
  });

  return (
    <Card>
      <SectionHead>
        <IconTile><BoxSvg /></IconTile>
        <div>
          <SectionTitle>{t('asm:assets.form.details.sectionTitle')}</SectionTitle>
          <SectionSub>{t('asm:assets.form.details.sectionSubtitle')}</SectionSub>
        </div>
      </SectionHead>
      <FieldGrid>
        <FieldWrap $full data-tour="asset-name">
          <Label>{t('asm:assets.form.name.label')} *</Label>
          <TextField
            fullWidth
            {...register('name', { required: t('asm:assets.form.name.required') })}
            placeholder={t('asm:assets.form.name.placeholder')}
            error={!!errors?.name}
            disabled={!!id}
            autoFocus={!id}
          />
          {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
        </FieldWrap>

        <FieldWrap data-tour="asset-criticity">
          <Label>{t('asm:assets.form.criticality.label')} *</Label>
          <Controller
            control={control}
            name="criticity"
            rules={{ validate: () => getValues('criticity') !== '' || t('asm:assets.form.criticality.required') }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="criticity"
                value={value}
                onChange={onChange}
                options={[
                  { value: '', label: t('asm:assets.form.criticality.placeholder') },
                  ...['1','2','3','4','5'].map(v => ({ value: v, label: t('asm:criticality.' + v) })),
                ]}
              />
            )}
          />
          {errors?.criticity && <ErrorMsg>{errors.criticity.message}</ErrorMsg>}
        </FieldWrap>

        <FieldWrap data-tour="asset-type">
          <Label>{t('asm:assets.form.type.label')} *</Label>
          <Controller
            control={control}
            name="type"
            rules={{ validate: () => getValues('type') !== '' || t('asm:assets.form.type.required') }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="type"
                value={value}
                onChange={onChange}
                options={[
                  { value: '', label: t('asm:assets.form.type.placeholder') },
                  { value: 'Pessoa', label: t('asm:assets.filter.typePerson') },
                  { value: 'Tecnologia', label: t('asm:assets.filter.typeTechnology') },
                  { value: 'Ambiente', label: t('asm:assets.filter.typeEnvironment') },
                ]}
              />
            )}
          />
          {errors?.type && <ErrorMsg>{errors.type.message}</ErrorMsg>}
        </FieldWrap>

        <FieldWrap data-tour="asset-user">
          <Label>{t('asm:assets.form.responsible.label')} *</Label>
          <Controller
            control={control}
            name="user"
            rules={{ validate: () => getValues('user') !== '' || t('asm:assets.form.responsible.required') }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="user"
                value={value}
                onChange={onChange}
                options={[
                  { value: '', label: t('asm:assets.form.responsible.placeholder') },
                  ...(users || []).map(u => ({ value: u.name, label: u.name })),
                ]}
              />
            )}
          />
          {errors?.user && <ErrorMsg>{errors.user.message}</ErrorMsg>}
        </FieldWrap>

        <FieldWrap data-tour="asset-tags">
          <Label>{t('asm:assets.form.tags.label')}</Label>
          <Controller
            control={control}
            name="tags"
            render={({ field: { onChange, value } }) => (
              <TagInput
                value={value}
                onChange={onChange}
                placeholder={t('asm:assets.form.tags.placeholder')}
              />
            )}
          />
        </FieldWrap>

        <FieldWrap data-tour="asset-location">
          <Label>{t('asm:assets.form.location.label')}</Label>
          <TextField
            fullWidth
            {...register('location')}
            placeholder={t('asm:assets.form.location.placeholder')}
          />
        </FieldWrap>

        <FieldWrap $full data-tour="asset-pii">
          <Controller
            control={control}
            name="pii"
            render={({ field: { onChange, value } }) => (
              <PiiCard>
                <Checkbox
                  checked={!!value}
                  onChange={e => onChange(e.target.checked)}
                />
                <div>
                  <p style={{ fontFamily: "'Maven Pro',sans-serif", fontSize: 14, fontWeight: 600, color: TEXT, margin: '0 0 3px' }}>
                    {t('asm:assets.form.pii.title')}
                  </p>
                  <p style={{ fontFamily: "'Lato',sans-serif", fontSize: 12, color: MUTED, margin: 0, lineHeight: 1.5 }}>
                    {t('asm:assets.form.pii.description')}
                  </p>
                </div>
              </PiiCard>
            )}
          />
        </FieldWrap>

        <FieldWrap $full data-tour="asset-description">
          <Label>{t('asm:assets.form.description.label')} *</Label>
          <TextField
            multiline
            fullWidth
            {...register('description', { required: t('asm:assets.form.description.required') })}
            rows={3}
            placeholder={t('asm:assets.form.description.placeholder')}
            error={!!errors?.description}
          />
          {errors?.description && <ErrorMsg>{errors.description.message}</ErrorMsg>}
        </FieldWrap>
      </FieldGrid>
    </Card>
  );
};
